// @flow
import moment from 'moment';

import Database from 'app/lib/Database';
import LegacyDispatcher from 'app/lib/legacyDispatcher';
import BaseStore from 'app/stores/BaseStore';
import UserStore from 'app/stores/UserStore';

const MASTER = "master";

declare var jQuery: {
  ajax: (options: { url: string, method: string, data: mixed }) => mixed
};

type Project = {
  id: string
};

type Branch = {
  name?: string,
  path: string,
  starred?: boolean,
  project?: Project,
  builds?: Array<Build>
};

type Build = {
  project: Project,
  branchName: string,
  branchPath: string,
  createdAt: string
};

export default class BranchStore extends BaseStore {
  branches: { [string]: Branch };

  constructor(project: Project, builds: Array<Build>) {
    // Call super on the parent store
    super();

    // Parse the project data
    this.project = Database.parse(project);

    // Create a database and seed it with data
    this.database = new Database(builds);

    // Index the builds based on branch
    this.branches = {};
    for (const build of this.database.all()) {
      this._parseBranchInformation(build);
    }

    // When we receive a build updates, update the database.
    LegacyDispatcher.on("build:started build:finished build:updated", ({ id, fetcher }) => {
      // Only request the data if it exists in the UI
      if (this.database.exists(id)) {
        fetcher({ truncate_jobs: true })
          .then((build) => this._loadAndEmit(build))
          .catch(() => {});
      }
    });
    // Do nothing

    LegacyDispatcher.on("build:created", ({ fetcher }) => {
      fetcher({ truncate_jobs: true })
        .then((build) => this._loadAndEmit(build))
        .catch(() => {});
    });
    // Do nothing

    LegacyDispatcher.on("project_branch:remove", ({ projectId, branch }) => {
      if (this.project.id === projectId) {
        // Fire and forget
        jQuery.ajax({
          url: `${this.project.path}/branches`,
          data: { branch },
          method: "DELETE"
        });
        delete this.branches[branch];
        this.emit("change");
      }
    });
  }

  all(): Array<Branch> {
    // The starred branches are in the order at which they should appear on the page
    let viewRecord;
    const starredBranches = [];
    this._starredBranches().forEach((branch) => {
      viewRecord = this._createBranchViewRecord(branch, true);
      if (viewRecord) {
        starredBranches.push(viewRecord);
      }
    });

    // The rest of the branches
    const nonStarredBranches = [];
    this._nonStarredBranches().forEach((branch) => {
      viewRecord = this._createBranchViewRecord(branch, false);
      if (viewRecord) {
        nonStarredBranches.push(viewRecord);
      }
    });

    // Return the view records
    return this._sortBranches(starredBranches).concat(this._sortBranches(nonStarredBranches));
  }

  getProject() {
    return this.project;
  }

  _sortBranches(branches: Array<Branch>): Array<Branch> {
    // First, split the branches up into master/non-master
    const masterBranch = [];
    const otherBranches = [];
    for (const branch of branches) {
      if (branch.name === MASTER) {
        masterBranch.push(branch);
      } else {
        otherBranches.push(branch);
      }
    }

    // Now sort the other branches by last build
    const sortedBranches = otherBranches.sort((branchA, branchB) => {
      if (!Array.isArray(branchA.builds) || branchA.builds.length === 0) {
        return -1;
      }

      const buildALastBuild = moment(branchA.builds[0].createdAt).unix();

      if (!Array.isArray(branchB.builds) || branchB.builds.length === 0) {
        return 1;
      }

      const buildBLastBuild = moment(branchB.builds[0].createdAt).unix();

      if (buildALastBuild === buildBLastBuild) {
        return 0;
      } else if (buildALastBuild >= buildBLastBuild) {
        return -1;
      }

      return 1;
    });

    // Return master first, followed by other branches
    return masterBranch.concat(sortedBranches);
  }

  _createBranchViewRecord(branchName: string, starred: boolean) {
    const branch = this.branches[branchName];
    if (!branch) {
      return null;
    }

    const builds = this._findBuildsForBranch(branchName);

    // Sort the builds on the branch by createdAt desc (newest first)
    const sortedBuilds = builds.sort((buildA, buildB) => moment(buildB.createdAt).unix() - moment(buildA.createdAt).unix());

    return {
      name: branchName,
      starred,
      path: branch.path,
      builds: sortedBuilds.slice(0, 5) // only show the last 5
    };
  }

  // Find the builds for a given branch
  _findBuildsForBranch(branchName: string) {
    return this.database.all().filter((build) => build.branchName === branchName);
  }

  // Stores information about the branch as we parse builds
  _parseBranchInformation(build: Build) {
    return this.branches[build.branchName] || (this.branches[build.branchName] = { path: build.branchPath });
  }

  _loadAndEmit(build: Build) {
    // We only care about this build if it matches the project
    if (build.project.id === this.project.id) {
      const newBuild = this.database.load(build);
      this._parseBranchInformation(newBuild);
      this.emit("change");
    }
  }

  _starredBranches() {
    return UserStore.getStarredBranches(this.project);
  }

  // Return non-starred branches that we know about.
  _nonStarredBranches(): Array<string> {
    const starredBranches = this._starredBranches();

    return Object.keys(this.branches).filter((branch) => starredBranches.indexOf(branch) === -1);
  }
}
